import React, { useState } from 'react';
import { CheckCircle2, Circle, Filter, Plus, Search, ListFilter, Calendar, AlertTriangle } from 'lucide-react';
import { useDeals } from '../../store/DealsContext';
import DueDiligenceList from './DueDiligenceList';
import DueDiligenceFilters from './DueDiligenceFilters';
import DueDiligenceProgress from './DueDiligenceProgress';
import DueDiligenceTimeline from './DueDiligenceTimeline';
import RiskAssessment from './RiskAssessment';
import TaskModal from './TaskModal';
import TaskDetails from './TaskDetails';
import { Task, Deal } from '../../types';

export default function DueDiligence() {
  const { deals, tasks, addTask, updateTask } = useDeals();
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [view, setView] = useState<'list' | 'timeline'>('list');

  const handleAddTask = (task: Omit<Task, 'id'>) => {
    const newTask: Task = {
      id: crypto.randomUUID(),
      ...task,
      progress: 0
    };
    addTask(newTask);
    setShowTaskModal(false);
  };

  const handleTaskUpdate = (taskId: string, updates: Partial<Task>) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      updateTask({ ...task, ...updates });
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !selectedStatus || task.status === selectedStatus;
    const matchesCategory = !selectedCategory || task.category === selectedCategory;
    const matchesDeal = !selectedDeal || task.dealId === selectedDeal.id;
    return matchesSearch && matchesStatus && matchesCategory && matchesDeal;
  });

  const categories = [
    { id: 'financial', name: 'Financial', color: 'blue' },
    { id: 'legal', name: 'Legal & Compliance', color: 'red' },
    { id: 'operational', name: 'Operational', color: 'green' },
    { id: 'hr', name: 'Human Resources', color: 'purple' },
    { id: 'it', name: 'IT & Systems', color: 'orange' },
    { id: 'environmental', name: 'Environmental', color: 'emerald' }
  ];

  const overdueTasks = filteredTasks.filter(task => 
    new Date(task.dueDate) < new Date() && task.status !== 'completed'
  );

  return (
    <div className="h-full space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Due Diligence Checklist</h2>
          <p className="mt-2 text-gray-600">Track and manage due diligence tasks</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 bg-white rounded-lg p-2">
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1 rounded-md ${
                view === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'
              }`}
            >
              List View
            </button>
            <button
              onClick={() => setView('timeline')}
              className={`px-3 py-1 rounded-md ${
                view === 'timeline' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'
              }`}
            >
              Timeline
            </button>
          </div>
          <button
            onClick={() => setShowTaskModal(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </button>
        </div>
      </div>

      <DueDiligenceProgress tasks={filteredTasks} />

      {overdueTasks.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <h3 className="text-sm font-medium text-red-800">
              Attention Required: {overdueTasks.length} Overdue Tasks
            </h3>
          </div>
          <div className="mt-2 space-y-2">
            {overdueTasks.slice(0, 3).map(task => (
              <div
                key={task.id}
                className="flex items-center justify-between text-sm text-red-600"
              >
                <span>{task.title}</span>
                <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <DueDiligenceFilters
            deals={deals}
            categories={categories}
            selectedDeal={selectedDeal}
            selectedCategory={selectedCategory}
            selectedStatus={selectedStatus}
            onDealChange={setSelectedDeal}
            onCategoryChange={setSelectedCategory}
            onStatusChange={setSelectedStatus}
            onSearch={setSearchTerm}
          />
        </div>

        {view === 'list' ? (
          <DueDiligenceList
            tasks={filteredTasks}
            categories={categories}
            onStatusChange={(taskId) => {
              const task = tasks.find(t => t.id === taskId);
              if (task) {
                const newStatus = task.status === 'completed' 
                  ? 'pending'
                  : task.status === 'in_progress'
                  ? 'completed'
                  : 'in_progress';
                handleTaskUpdate(taskId, { status: newStatus });
              }
            }}
            onTaskSelect={setSelectedTask}
          />
        ) : (
          <DueDiligenceTimeline tasks={filteredTasks} categories={categories} />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RiskAssessment tasks={filteredTasks} />
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Category Distribution</h3>
          <div className="space-y-4">
            {categories.map(category => {
              const categoryTasks = filteredTasks.filter(t => t.category === category.id);
              const completedTasks = categoryTasks.filter(t => t.status === 'completed');
              const percentage = categoryTasks.length 
                ? (completedTasks.length / categoryTasks.length) * 100 
                : 0;
              
              return (
                <div key={category.id}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-600">
                      {category.name}
                    </span>
                    <span className="text-sm text-gray-500">
                      {completedTasks.length}/{categoryTasks.length}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full">
                    <div
                      className={`h-2 bg-${category.color}-600 rounded-full`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showTaskModal && (
        <TaskModal
          deals={deals}
          categories={categories}
          onSubmit={handleAddTask}
          onClose={() => setShowTaskModal(false)}
        />
      )}

      {selectedTask && (
        <TaskDetails
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
          categories={categories}
        />
      )}
    </div>
  );
}